import { useCallback } from 'react';
import { toast } from 'react-toastify';

/**
 * Custom hook for displaying toast notifications
 * Uses react-toastify under the hood
 * @returns {Object} Toast utility methods
 */
export const useToast = () => {
  /**
   * Show a toast notification
   * @param {string} message - Message to display 
   * @param {string} type - Toast type: 'success', 'error', 'info', 'warning'
   * @param {Object} options - Additional toast options
   */
  const showToast = useCallback((message, type = 'info', options = {}) => {
    const defaultOptions = {
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    };
    
    const toastOptions = { ...defaultOptions, ...options };
    
    switch (type) {
      case 'success':
        toast.success(message, toastOptions);
        break;
      case 'error':
        toast.error(message, toastOptions);
        break;
      case 'warning':
        toast.warning(message, toastOptions);
        break;
      case 'info':
      default:
        toast.info(message, toastOptions);
        break;
    }
  }, []);
  
  /**
   * Show a success toast
   * @param {string} message - Success message
   * @param {Object} options - Additional toast options
   */
  const showSuccess = useCallback((message, options = {}) => {
    showToast(message, 'success', options);
  }, [showToast]);
  
  /**
   * Show an error toast
   * @param {string} message - Error message
   * @param {Object} options - Additional toast options
   */
  const showError = useCallback((message, options = {}) => {
    showToast(message, 'error', options);
  }, [showToast]);
  
  /**
   * Show a warning toast
   * @param {string} message - Warning message
   * @param {Object} options - Additional toast options
   */
  const showWarning = useCallback((message, options = {}) => {
    showToast(message, 'warning', options);
  }, [showToast]);
  
  /**
   * Show an info toast
   * @param {string} message - Info message
   * @param {Object} options - Additional toast options
   */
  const showInfo = useCallback((message, options = {}) => {
    showToast(message, 'info', options);
  }, [showToast]);
  
  /**
   * Show a toast for API errors
   * @param {Error} error - Error object from API request
   * @param {string} fallbackMessage - Fallback message if error response doesn't have a message
   */
  const showApiError = useCallback((error, fallbackMessage = 'An error occurred') => {
    const errorMessage = error.response?.data?.message || 
                        error.message || 
                        fallbackMessage;
    
    showToast(errorMessage, 'error');
  }, [showToast]);
  
  return {
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showApiError
  };
};

export default useToast;
